import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { apiErrorMessage } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Zap, Sparkles,
  TrendingUp, Smile, Minimize2, Cpu, MapPin, Crown, Check, Loader2,
  Upload, X, Film, Globe
} from "lucide-react";
import { Flag } from "../components/LocaleSwitcher";
import { useLocale } from "../context/LocaleContext";
import { LANGUAGES } from "../i18n/config";

function AdLanguageNotice() {
  const { t } = useTranslation();
  const { language } = useLocale();
  const lang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  return (
    <div className="mt-3 flex items-center gap-2" data-testid="ad-language-notice">
      <Globe className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#6b6b6b" }} />
      <span className="text-xs" style={{ color: "#6b6b6b" }}>
        {t("locale.adLanguageHint", { language: lang.native })}
      </span>
      <Flag code={lang.flag} className="w-4 h-3" />
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  "Food & Beverage", "Retail", "Beauty & Skincare", "Health & Wellness",
  "Technology", "Real Estate", "Fitness", "Education", "Fashion",
  "Home Services", "Entertainment", "Professional Services", "Other"
];

const PLATFORMS = [
  { id: "TikTok", label: "TikTok", emoji: "♪" },
  { id: "Instagram Reels", label: "Instagram Reels", emoji: "◉" },
  { id: "YouTube Shorts", label: "YouTube Shorts", emoji: "▶" },
  { id: "Snapchat", label: "Snapchat", emoji: "◈" },
  { id: "LinkedIn", label: "LinkedIn", emoji: "in" },
  { id: "X / Twitter", label: "X / Twitter", emoji: "X" },
];

const TONES = [
  { id: "Bold", icon: TrendingUp, color: "text-red-400 bg-red-500/10 border-red-500/20" },
  { id: "Luxury", icon: Crown, color: "text-amber-400 bg-amber-500/10 border-amber-500/20" },
  { id: "Funny", icon: Smile, color: "text-lime-400 bg-lime-500/10 border-lime-500/20" },
  { id: "Minimal", icon: Minimize2, color: "text-zinc-400 bg-zinc-700/30 border-zinc-600" },
  { id: "Tech", icon: Cpu, color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
  { id: "Local", icon: MapPin, color: "text-green-400 bg-green-500/10 border-green-500/20" },
];

// Suggestion chips + step labels live in the locale files (newProject.suggestions / newProject.steps)
function useSuggestions() {
  const { t } = useTranslation();
  return t("newProject.suggestions", { returnObjects: true });
}

// Small reusable chip row
function SuggestionChips({ items, onSelect, field }) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {items.map((s, i) => (
        <button
          key={`${field}-${s}`}
          type="button"
          onClick={() => onSelect(field, s)}
          data-testid={`suggestion-${field}-${i}`}
          className="text-xs px-2.5 py-1 rounded-full border transition-all hover:border-purple-500/60 hover:text-purple-300 hover:bg-purple-500/10 active:scale-95"
          style={{ background: "#1a1a1a", borderColor: "#6b6b6b", color: "#5a5a5a" }}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

const defaultForm = {
  business_name: "",
  industry: "",
  description: "",
  offer: "",
  website: "",
  location: "",
  competitor: "",
  platforms: [],
  tone: "",
  reference_style: "",
  reference_image_url: null,
};

// Wizard progress survives refresh: form, draft id and current step live in localStorage
function loadLocalDraft() {
  try {
    const form = JSON.parse(localStorage.getItem("rl_draft_form") || "null");
    return { form: form ? { ...defaultForm, ...form } : null, id: localStorage.getItem("rl_draft_id"),
             step: Number(localStorage.getItem("rl_draft_step")) || 1 };
  } catch {
    return { form: null, id: null, step: 1 };
  }
}

// Never resume past a step whose requirements aren't met
function safeStep(step, form, id) {
  if (!id || step < 2) return 1;
  return step >= 3 && form.platforms?.length && form.tone ? 3 : 2;
}

function StepIndicator({ current, total }) {
  const { t } = useTranslation();
  const labels = t("newProject.steps", { returnObjects: true });
  return (
    <div className="flex items-center gap-2 mb-10">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
            i + 1 === current
              ? "bg-purple-600 text-white shadow-md shadow-purple-500/30"
              : i + 1 < current
              ? "bg-lime-500/20 text-lime-400"
              : "bg-zinc-800 text-zinc-400"
          }`}>
            {i + 1 < current ? "✓" : i + 1}
          </div>
          <span className={`text-sm ${i + 1 === current ? "font-semibold text-white" : "text-zinc-400"} hidden sm:block`}>
            {labels[i]}
          </span>
          {i < total - 1 && <div className="flex-1 h-0.5 mx-2 w-8 hidden sm:block" style={{ background: "#252525" }} />}
        </div>
      ))}
    </div>
  );
}

export default function NewProject() {
  const { t } = useTranslation();
  const { language } = useLocale();
  const SUGGESTIONS = useSuggestions();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get("draft");
  const [local] = useState(loadLocalDraft);
  const fromLocal = !!local.form && (!resumeId || local.id === resumeId);
  const [step, setStep] = useState(() => (fromLocal ? safeStep(local.step, local.form, local.id) : 1));
  const [form, setForm] = useState(() => (fromLocal ? local.form : defaultForm));
  const [loading, setLoading] = useState(false);
  const [brandKitLoaded, setBrandKitLoaded] = useState(false);
  const [draftId, setDraftId] = useState(() => (fromLocal ? local.id : null));
  const [saveStatus, setSaveStatus] = useState("idle"); // "idle" | "saving" | "saved"
  const [refFiles, setRefFiles] = useState([]);         // [{ name, preview, type, file }]
  const [refAnalyzing, setRefAnalyzing] = useState(false);
  const debounceRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  // Fill a field from a suggestion chip
  const fillSuggestion = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // Handle reference file upload + auto-analyze (up to 3 files, one Gemini call)
  const handleReferenceUpload = async (newFiles) => {
    if (!newFiles || newFiles.length === 0) return;
    const toAdd = Array.from(newFiles).filter(f => {
      const isImage = f.type.startsWith("image/");
      const isVideo = f.type.startsWith("video/");
      if (!isImage && !isVideo) {
        toast.error(t("newProject.toasts.badFileType", { name: f.name }));
        return false;
      }
      return true;
    });
    if (!toAdd.length) return;

    // Keep existing entries as-is (they wrap their File) and only wrap the newly picked files
    const fresh = toAdd.map(f => ({
      id: `${f.name}-${f.size}-${f.lastModified ?? ""}`,
      name: f.name,
      preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      type: f.type.startsWith("image/") ? "image" : "video",
      file: f,
    }));
    analyseReferences([...refFiles, ...fresh].slice(0, 3));
  };

  // One Gemini call for the current set of references; on failure the previous set is restored
  const analyseReferences = async (newEntries) => {
    const previous = { files: refFiles, style: form.reference_style, url: form.reference_image_url };
    setRefFiles(newEntries);
    if (!newEntries.length) {
      setForm(f => ({ ...f, reference_style: "", reference_image_url: null }));
      return;
    }
    setRefAnalyzing(true);
    setForm(f => ({ ...f, reference_style: "", reference_image_url: null }));
    try {
      const fd = new FormData();
      newEntries.forEach(e => fd.append("files", e.file));
      const res = await axios.post(`${API}/analyze-reference`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const style = res.data.style_description || "";
      const refImageUrl = res.data.reference_image_url || null;
      setForm(f => ({ ...f, reference_style: style, reference_image_url: refImageUrl }));
      toast.success(newEntries.length > 1 ? t("newProject.toasts.refsAnalysed") : t("newProject.toasts.refAnalysed"));
    } catch (err) {
      toast.error(apiErrorMessage(err, t("newProject.toasts.refFailed")));
      setRefFiles(previous.files);
      setForm(f => ({ ...f, reference_style: previous.style, reference_image_url: previous.url }));
    } finally {
      setRefAnalyzing(false);
    }
  };

  const removeReference = (idx) => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    analyseReferences(refFiles.filter((_, i) => i !== idx));
  };

  // Resume a draft opened from the Dashboard when this browser has no local copy of it
  useEffect(() => {
    if (!resumeId || fromLocal) return;
    axios.get(`${API}/projects/${resumeId}`, { withCredentials: true }).then(({ data }) => {
      if (data.status !== "pending") { navigate(`/projects/${resumeId}`, { replace: true }); return; }
      const f = { ...defaultForm };
      Object.keys(defaultForm).forEach(k => { if (data[k] != null) f[k] = data[k]; });
      localStorage.setItem("rl_draft_id", resumeId);
      setForm(f);
      setDraftId(resumeId);
      setStep(safeStep(3, f, resumeId));
    }).catch(err => toast.error(apiErrorMessage(err, t("newProject.toasts.createFailed"))));
  }, []); // eslint-disable-line

  useEffect(() => { localStorage.setItem("rl_draft_step", String(step)); }, [step]);

  // Auto-save form to localStorage while typing (debounced 600ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      localStorage.setItem("rl_draft_form", JSON.stringify(form));
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [form]);

  // Pre-fill form from saved brand kit
  useEffect(() => {
    const loadBrandKit = async () => {
      try {
        const res = await axios.get(`${API}/brand-kit`, { withCredentials: true });
        if (res.data) {
          setForm(f => ({
            ...f,
            business_name: f.business_name || res.data.business_name || "",
            tone: f.tone || res.data.tone || "",
          }));
          if (res.data.business_name || res.data.tone) setBrandKitLoaded(true);
        }
      } catch (err) {
        // Brand kit is optional — log but don't surface to user
        console.debug("Brand kit not available:", err?.response?.status);
      }
    };
    loadBrandKit();
  }, []);

  const togglePlatform = (id) => {
    setForm(f => ({
      ...f,
      platforms: f.platforms.includes(id)
        ? f.platforms.filter(p => p !== id)
        : [...f.platforms, id]
    }));
  };

  const applyLazyMode = () => {
    setForm(f => ({ ...f, platforms: ["TikTok", "Instagram Reels"], tone: "Bold" }));
    toast.info(t("newProject.toasts.lazyMode"));
  };

  const validateStep = () => {
    if (step === 1) {
      if (!form.business_name.trim()) { toast.error(t("newProject.errors.businessName")); return false; }
      if (!form.industry) { toast.error(t("newProject.errors.industry")); return false; }
      if (!form.description.trim()) { toast.error(t("newProject.errors.description")); return false; }
    }
    if (step === 2) {
      if (form.platforms.length === 0) { toast.error(t("newProject.errors.platforms")); return false; }
      if (!form.tone) { toast.error(t("newProject.errors.tone")); return false; }
    }
    return true;
  };

  // Ad copy is generated in the current UI language
  const payload = () => ({ ...form, language });

  // Save to backend when moving from step 1 → step 2
  const handleNextStep1 = useCallback(async () => {
    if (!validateStep()) return;
    setSaveStatus("saving");
    try {
      if (!draftId) {
        const res = await axios.post(`${API}/projects`, payload(), { withCredentials: true });
        const id = res.data.project_id;
        setDraftId(id);
        localStorage.setItem("rl_draft_id", id);
      } else {
        try {
          await axios.patch(`${API}/projects/${draftId}`, payload(), { withCredentials: true });
        } catch (patchErr) {
          if (patchErr.response?.status === 404) {
            // Stale draft — create fresh and update state
            const res = await axios.post(`${API}/projects`, payload(), { withCredentials: true });
            setDraftId(res.data.project_id);
            localStorage.setItem("rl_draft_id", res.data.project_id);
          }
          // Non-blocking — still advance on other errors
        }
      }
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (err) {
      console.error("Failed to save draft project:", err);
      setSaveStatus("idle");
    }
    setStep(2);
  }, [form, draftId, language]); // eslint-disable-line

  const handleGenerate = async () => {
    setLoading(true);
    try {
      let pid = draftId;
      if (!pid) {
        const createRes = await axios.post(`${API}/projects`, payload(), { withCredentials: true });
        pid = createRes.data.project_id;
      } else {
        try {
          // Patch latest form data (platforms/tone may have changed in step 2)
          await axios.patch(`${API}/projects/${pid}`, payload(), { withCredentials: true });
        } catch (patchErr) {
          if (patchErr.response?.status === 404) {
            // Stale draft ID in localStorage — create a fresh project
            const createRes = await axios.post(`${API}/projects`, payload(), { withCredentials: true });
            pid = createRes.data.project_id;
          } else {
            throw patchErr;
          }
        }
      }
      await axios.post(`${API}/projects/${pid}/generate`, {}, { withCredentials: true });
      // Clear draft from localStorage
      localStorage.removeItem("rl_draft_form");
      localStorage.removeItem("rl_draft_id");
      localStorage.removeItem("rl_draft_step");
      toast.success(t("newProject.toasts.generating"));
      navigate(`/projects/${pid}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = apiErrorMessage(err, t("newProject.toasts.createFailed"));
      if (err.response?.status === 403 && (detail === "subscription_required" || detail === "subscription_required_for_topup")) {
        toast.error(t("newProject.toasts.subscriptionRequired"));
        navigate("/billing");
      } else if (err.response?.status === 402) {
        toast.error(msg);
        navigate("/billing");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <StepIndicator current={step} total={3} />

        <AnimatePresence mode="wait">
          {/* STEP 1: Business & Offer */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
              className="space-y-5"
            >
              <div>
                <div className="flex items-center justify-between">
                  <h2 className="text-3xl font-black text-white mb-1" style={{ fontFamily: "Epilogue, sans-serif" }}>
                    {t("newProject.step1.title")}
                  </h2>
                  {saveStatus === "saving" && (
                    <span className="flex items-center gap-1.5 text-xs" style={{ color: "#6b6b6b" }}>
                      <Loader2 className="w-3 h-3 animate-spin" /> {t("common.saving")}
                    </span>
                  )}
                  {saveStatus === "saved" && (
                    <span className="flex items-center gap-1.5 text-xs" style={{ color: "#a3a3a3" }}>
                      <Check className="w-3 h-3 text-lime-400" /> {t("newProject.step1.draftSaved")}
                    </span>
                  )}
                </div>
                <p className="text-zinc-400 text-sm">{t("newProject.step1.subtitle")}</p>
                {brandKitLoaded && (
                  <div className="mt-2 inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full" style={{ background: "rgba(168,85,247,0.1)", border: "1px solid rgba(168,85,247,0.25)", color: "#c084fc" }}>
                    <Sparkles className="w-3 h-3" /> {t("newProject.step1.brandKitApplied")}
                  </div>
                )}
                <AdLanguageNotice />
              </div>

              <div>
                <Label className="text-sm font-semibold text-zinc-300">{t("newProject.step1.businessName")} <span className="text-red-400">*</span></Label>
                <Input
                  placeholder={t("newProject.step1.businessNamePlaceholder")}
                  value={form.business_name}
                  onChange={e => setForm({ ...form, business_name: e.target.value })}
                  data-testid="input-business-name"
                  dir="auto"
                  className="mt-1 h-11 bg-zinc-900 border-zinc-700 rounded-xl focus:border-zinc-600 focus:ring-neutral-100"
                />
                <SuggestionChips
                  field="business_name"
                  items={(SUGGESTIONS.business_name[form.industry] || SUGGESTIONS.business_name.default)}
                  onSelect={fillSuggestion}
                />
              </div>

              <div>
                <Label className="text-sm font-semibold text-zinc-300">{t("newProject.step1.industry")} <span className="text-red-400">*</span></Label>
                <Select value={form.industry} onValueChange={val => setForm({ ...form, industry: val })}>
                  <SelectTrigger className="mt-1 h-11 rounded-xl text-zinc-300" style={{ background: "#1c1c1c", border: "1px solid #333" }} data-testid="select-industry">
                    <SelectValue placeholder={t("newProject.step1.industryPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(ind => (
                      <SelectItem key={ind} value={ind}>{t(`industries.${ind}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-semibold text-zinc-300">{t("newProject.step1.description")} <span className="text-red-400">*</span></Label>
                <Textarea
                  placeholder={t("newProject.step1.descriptionPlaceholder")}
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  data-testid="input-description"
                  dir="auto"
                  className="mt-1 bg-zinc-900 border-zinc-700 rounded-xl min-h-[90px] focus:border-zinc-600 focus:ring-neutral-100 resize-none"
                />
                <SuggestionChips
                  field="description"
                  items={(SUGGESTIONS.description[form.industry] || SUGGESTIONS.description.default)}
                  onSelect={fillSuggestion}
                />
              </div>

              <div>
                <Label className="text-sm font-medium text-zinc-400">{t("newProject.step1.offer")} <span className="text-zinc-400">({t("common.optional").toLowerCase()})</span></Label>
                <Input
                  placeholder={t("newProject.step1.offerPlaceholder")}
                  value={form.offer}
                  onChange={e => setForm({ ...form, offer: e.target.value })}
                  data-testid="input-offer"
                  dir="auto"
                  className="mt-1 h-11 bg-zinc-900 border-zinc-700 rounded-xl text-white"
                />
                <SuggestionChips field="offer" items={SUGGESTIONS.offer} onSelect={fillSuggestion} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-zinc-400">{t("newProject.step1.website")} <span className="text-zinc-400">({t("common.optional").toLowerCase()})</span></Label>
                  <Input
                    placeholder="@yourbrand"
                    value={form.website}
                    onChange={e => setForm({ ...form, website: e.target.value })}
                    data-testid="input-website"
                    className="mt-1 h-11 bg-zinc-900 border-zinc-700 rounded-xl text-white"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-zinc-400">{t("newProject.step1.location")} <span className="text-zinc-400">({t("common.optional").toLowerCase()})</span></Label>
                  <Input
                    placeholder={t("newProject.step1.locationPlaceholder")}
                    value={form.location}
                    onChange={e => setForm({ ...form, location: e.target.value })}
                    data-testid="input-location"
                    className="mt-1 h-11 bg-zinc-900 border-zinc-700 rounded-xl text-white"
                  />
                  <SuggestionChips field="location" items={SUGGESTIONS.location} onSelect={fillSuggestion} />
                </div>
              </div>

              {/* Competitor Inspiration */}
              <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
                <Label className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  {t("newProject.step1.competitor")} <span className="text-zinc-400 font-normal">({t("common.optional").toLowerCase()})</span>
                </Label>
                <Textarea
                  placeholder={t("newProject.step1.competitorPlaceholder")}
                  value={form.competitor}
                  onChange={e => setForm({ ...form, competitor: e.target.value })}
                  data-testid="input-competitor"
                  dir="auto"
                  className="mt-2 bg-zinc-900 border-zinc-700 rounded-xl min-h-[70px] focus:border-zinc-600 resize-none text-sm"
                />
                <p className="text-xs text-zinc-400 mt-1.5">{t("newProject.step1.competitorHint")}</p>
              </div>

              <Button
                onClick={handleNextStep1}
                data-testid="step1-next-btn"
                disabled={saveStatus === "saving"}
                className="w-full h-12 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saveStatus === "saving"
                  ? <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> {t("common.saving")}</>
                  : <>{t("newProject.step1.next")} <ArrowRight className="ml-2 w-5 h-5 rtl:rotate-180" /></>
                }
              </Button>
            </motion.div>
          )}

          {/* STEP 2: Style & Platform */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
              className="space-y-6"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-3xl font-black text-white mb-1" style={{ fontFamily: "Epilogue, sans-serif" }}>
                    {t("newProject.step2.title")}
                  </h2>
                  <p className="text-zinc-400 text-sm">{t("newProject.step2.subtitle")}</p>
                </div>
                <button
                  onClick={applyLazyMode}
                  data-testid="lazy-mode-btn"
                  className="flex items-center gap-1.5 text-xs font-bold bg-lime-400 hover:bg-lime-300 text-white px-3 py-2 rounded-full transition-colors"
                >
                  <Zap className="w-3.5 h-3.5" /> {t("newProject.step2.lazyMode")}
                </button>
              </div>

              <div>
                <Label className="text-sm font-semibold text-zinc-300 mb-3 block">
                  {t("newProject.step2.platforms")} <span className="text-red-400">*</span>
                  <span className="text-zinc-400 font-normal ml-1">{t("newProject.step2.selectAll")}</span>
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {PLATFORMS.map(p => (
                    <button
                      key={p.id}
                      onClick={() => togglePlatform(p.id)}
                      data-testid={`platform-${p.id.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        form.platforms.includes(p.id)
                          ? "border-purple-500 bg-purple-500/10"
                          : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                      }`}
                    >
                      <div className="text-xl mb-1">{p.emoji}</div>
                      <div className="font-semibold text-white text-sm">{p.label}</div>
                      <div className="text-zinc-400 text-xs">{t(`platformDescriptions.${p.id}`)}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-zinc-300 mb-3 block">
                  {t("newProject.step2.tone")} <span className="text-red-400">*</span>
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {TONES.map(tone => (
                    <button
                      key={tone.id}
                      onClick={() => setForm(f => ({ ...f, tone: tone.id }))}
                      data-testid={`tone-${tone.id.toLowerCase()}`}
                      className={`p-4 rounded-xl border-2 text-left transition-all ${
                        form.tone === tone.id
                          ? "border-purple-500 bg-purple-500/10"
                          : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${tone.color.split(" ").slice(1).join(" ")}`}>
                        <tone.icon className={`w-4 h-4 ${tone.color.split(" ")[0]}`} />
                      </div>
                      <div className="font-semibold text-white text-sm">{t(`tones.${tone.id}`)}</div>
                      <div className="text-zinc-400 text-xs">{t(`toneDescriptions.${tone.id}`)}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Style Reference Upload */}
              <div className="rounded-xl p-4 border border-zinc-700" style={{ background: "#0e0e0e" }}>
                <Label className="text-sm font-semibold text-zinc-200 flex items-center gap-2 mb-1">
                  <Film className="w-4 h-4 text-purple-400" />
                  {t("newProject.step2.styleRef")} <span className="text-zinc-400 font-normal">({t("common.optional").toLowerCase()})</span>
                </Label>
                <p className="text-xs text-zinc-400 mb-1">{t("newProject.step2.styleRefHint1")}</p>
                <p className="text-xs mb-3" style={{ color: "#6b6b6b" }}>{t("newProject.step2.styleRefHint2")}</p>

                <div className="space-y-2">
                  {/* After a refresh the files are gone but the extracted style is kept */}
                  {!refFiles.length && !refAnalyzing && form.reference_style && (
                    <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: "#1a1a1a" }} data-testid="ref-saved-row">
                      <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center" style={{ background: "#252525" }}>
                        <Check className="w-5 h-5 text-lime-400" />
                      </div>
                      <p className="flex-1 text-xs text-lime-400">{t("newProject.step2.styleExtracted")}</p>
                      <button type="button" onClick={() => setForm(f => ({ ...f, reference_style: "", reference_image_url: null }))}
                        data-testid="ref-saved-clear-btn" className="text-zinc-400 hover:text-zinc-300 transition-colors flex-shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                  {refFiles.map((rf, idx) => (
                    <div key={rf.id || `${rf.name}-${idx}`} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: "#1a1a1a" }}>
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ background: "#252525" }}>
                        {rf.preview
                          ? <img src={rf.preview} alt="ref" className="w-full h-full object-cover" />
                          : <Film className="w-5 h-5 text-purple-400" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-zinc-300 truncate">{rf.name}</p>
                        {refAnalyzing && <p className="text-xs text-purple-400 flex items-center gap-1 mt-0.5"><Loader2 className="w-3 h-3 animate-spin" /> {t("newProject.step2.analysing")}</p>}
                        {!refAnalyzing && form.reference_style && <p className="text-xs text-lime-400 flex items-center gap-1 mt-0.5"><Check className="w-3 h-3" /> {t("newProject.step2.styleExtracted")}</p>}
                      </div>
                      {!refAnalyzing && (
                        <button type="button" onClick={() => removeReference(idx)} data-testid={`ref-remove-btn-${idx}`} className="text-zinc-400 hover:text-zinc-300 transition-colors flex-shrink-0">
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}

                  {refFiles.length < 3 && !refAnalyzing && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="ref-upload-btn"
                      className="w-full border-2 border-dashed border-zinc-700 rounded-xl py-5 flex flex-col items-center gap-1.5 transition-colors hover:border-purple-500/50 hover:bg-purple-500/5"
                    >
                      <Upload className="w-5 h-5 text-zinc-400" />
                      <span className="text-sm text-zinc-400">{refFiles.length === 0 ? t("newProject.step2.uploadRef") : t("newProject.step2.addRef")}</span>
                      <span className="text-xs text-zinc-500">{t("newProject.step2.refFormats", { count: 3 - refFiles.length })}</span>
                    </button>
                  )}
                </div>

                {!refAnalyzing && form.reference_style && (
                  <div className="mt-3">
                    <Label className="text-xs text-zinc-400 mb-1 block">{t("newProject.step2.extractedStyle")}</Label>
                    <Textarea
                      value={form.reference_style}
                      onChange={e => setForm(f => ({ ...f, reference_style: e.target.value }))}
                      data-testid="input-reference-style"
                      className="bg-zinc-900 border-zinc-700 rounded-xl text-xs text-zinc-300 resize-none"
                      rows={3}
                    />
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
                  multiple
                  className="hidden"
                  onChange={e => handleReferenceUpload(e.target.files)}
                  data-testid="ref-file-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  data-testid="step2-back-btn"
                  className="flex-1 h-12 border-zinc-700 rounded-xl"
                >
                  <ArrowLeft className="mr-2 w-4 h-4 rtl:rotate-180" /> {t("common.back")}
                </Button>
                <Button
                  onClick={() => validateStep() && setStep(3)}
                  data-testid="step2-next-btn"
                  disabled={refAnalyzing}
                  className="flex-1 h-12 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {refAnalyzing
                    ? <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> {t("newProject.step2.analysingStyle")}</>
                    : <>{t("newProject.step2.next")} <ArrowRight className="ml-2 w-4 h-4 rtl:rotate-180" /></>
                  }
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 3: Review & Generate */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25 }}
              className="space-y-6"
            >
              <div>
                <h2 className="text-3xl font-black text-white mb-1" style={{ fontFamily: "Epilogue, sans-serif" }}>
                  {t("newProject.step3.title")}
                </h2>
                <p className="text-zinc-400 text-sm">{t("newProject.step3.subtitle")}</p>
              </div>

              {/* Summary card */}
              <div className="bg-zinc-900 rounded-2xl border border-zinc-700 p-6 space-y-4">
                <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider">{t("newProject.step3.brief")}</h3>
                {[
                  { label: t("newProject.step3.business"), value: form.business_name },
                  { label: t("newProject.step1.industry"), value: t(`industries.${form.industry}`, { defaultValue: form.industry }) },
                  { label: t("newProject.step3.descriptionLabel"), value: form.description },
                  form.offer && { label: t("newProject.step3.offer"), value: form.offer },
                  form.location && { label: t("newProject.step3.location"), value: form.location },
                ].filter(Boolean).map(item => (
                  <div key={item.label} className="flex gap-3">
                    <span className="text-xs font-semibold text-zinc-400 uppercase w-20 flex-shrink-0 pt-0.5">{item.label}</span>
                    <span className="text-zinc-200 text-sm flex-1">{item.value}</span>
                  </div>
                ))}
                <div className="flex gap-3">
                  <span className="text-xs font-semibold text-zinc-400 uppercase w-20 flex-shrink-0 pt-0.5">{t("newProject.step2.platforms")}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {form.platforms.map(p => (
                      <span key={p} className="text-xs px-2 py-0.5 rounded-full" style={{ background: "#252525", color: "#d4d4d8", border: "1px solid #333" }}>{p}</span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="text-xs font-semibold text-zinc-400 uppercase w-20 flex-shrink-0 pt-0.5">{t("newProject.step3.tone")}</span>
                  <span className="text-xs bg-zinc-900 text-zinc-300 px-2 py-0.5 rounded-full font-semibold">{t(`tones.${form.tone}`, { defaultValue: form.tone })}</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-xs font-semibold text-zinc-400 uppercase w-20 flex-shrink-0 pt-0.5">{t("locale.adLanguage")}</span>
                  <span className="text-xs bg-zinc-900 text-zinc-300 px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1.5" data-testid="brief-ad-language">
                    <Flag code={(LANGUAGES.find(l => l.code === language) || LANGUAGES[0]).flag} className="w-4 h-3" />
                    {(LANGUAGES.find(l => l.code === language) || LANGUAGES[0]).native}
                  </span>
                </div>
              </div>

              {/* Expected output */}
              <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-zinc-300" />
                  <span className="text-sm font-bold text-zinc-200">{t("newProject.step3.youReceive")}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {t("newProject.step3.outputs", { returnObjects: true }).map(item => (
                    <div key={item} className="flex items-center gap-2 text-xs text-zinc-300">
                      <div className="w-1.5 h-1.5 bg-neutral-400 rounded-full" />
                      {item}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-zinc-400 mt-3">{t("newProject.step3.eta")}</p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  data-testid="step3-back-btn"
                  className="flex-1 h-12 border-zinc-700 rounded-xl"
                >
                  <ArrowLeft className="mr-2 w-4 h-4 rtl:rotate-180" /> {t("common.back")}
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={loading}
                  data-testid="generate-ads-btn"
                  className="flex-1 h-12 bg-purple-600 hover:bg-purple-500 text-white font-black rounded-xl text-base"
                >
                  {loading ? (
                    <>{t("newProject.step3.launching")}</>
                  ) : (
                    <><Sparkles className="mr-2 w-5 h-5" /> {t("newProject.step3.generate")}
                      <span className="px-1.5 py-0.5 rounded font-bold ml-2 inline-flex items-center" style={{ background: "rgba(124,58,237,0.3)", color: "#ede9fe", fontSize: "0.8em", lineHeight: 1.4 }}>2cr</span>
                    </>
                  )}
                </Button>
              </div>
              <p className="text-center text-xs" style={{ color: "#6b6b6b" }}>
                {t("newProject.step3.creditNote")}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
